import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { motion } from 'framer-motion'
import { GoogleLogin } from '@react-oauth/google'
import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { SplitAuthLayout } from '@/components/auth/SplitAuthLayout.jsx'
import { AuthInput, AuthField, AuthButton, MicroLabel } from '@/components/auth/AuthPrimitives.jsx'
import { CircleLoader } from '@/components/loader/CircleLoader.jsx'

import { useAuthContext } from '@/context-state/AuthContext.jsx'
import { useRegister } from '@/hooks/useRegister.jsx'

import * as API from '@/services/api.js'
import { handleError } from '@/services/errorHandler.js'

import { BASE_PATH_AFTER_SUCCESSFUL_AUTH } from '@/consts/index.js'
import { SHOPIFY_SCOPE, SHOPIFY_CLIENT_ID } from '@/config-global.jsx'

import Logo from '@/assets/logos/upspring-dark.png'
import { LegalApproval } from '@/pages/auth/LegalApproval.jsx'

const SIGN_IN_SLIDE = {
    id: 'sign-in',
    glow: 'a',
    eyebrow: 'Welcome back',
    title: 'The creative context layer for your AI.',
    subtitle: 'Sign in to pick up where your team left off.',
}

const SignInPage = () => {

    const { authenticate, user } = useAuthContext()
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(true)
    const [showMobileOverlay, setShowMobileOverlay] = useState(false)
    const { handleGoogleLogin, handleGoogleLoginError } = useRegister({ setIsLoading, setShowMobileOverlay })

    const formSchema = z.object({
        email: z.string().min(1, 'Email is required').email('Invalid email'),
        password: z.string().min(1, 'Password is required'),
    })

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: '',
            password: '',
        },
    })

    // Shopify INSTALL entry-point: if we arrive with `?shop=`, redirect straight
    // into Shopify's OAuth authorize screen instead of showing the login form.
    const initAuth = async () => {
        try {
            const queryParams = new URLSearchParams(window.location.search)
            const shopDomain = queryParams.get('shop')

            if (user && !shopDomain) {
                navigate(BASE_PATH_AFTER_SUCCESSFUL_AUTH)
                return
            }

            if (shopDomain) {
                const redirect_uri = `${window.location.origin}/connect/shopify`
                const shopifyAuthUrl = `https://${shopDomain}/admin/oauth/authorize?client_id=${SHOPIFY_CLIENT_ID}&scope=${SHOPIFY_SCOPE}&redirect_uri=${redirect_uri}&state=abcc`
                window.location.replace(shopifyAuthUrl)
                return
            }

            setIsLoading(false)
        } catch (e) {
            toast.error(e.message)
            handleError(e)
            setIsLoading(false)
        }
    }

    const manuallyLogIn = async (formData) => {
        try {
            setIsLoading(true)
            const { data } = await API.logInUser({ email: formData.email.toLowerCase(), password: formData.password })

            await authenticate(data)
            toast.success('Welcome back to Upspring!')
            navigate(BASE_PATH_AFTER_SUCCESSFUL_AUTH)
        } catch (e) {
            toast.error(e.message)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        initAuth()
    }, [user])

    return (
        <>
            {showMobileOverlay && (
                <div className="fixed left-0 top-0 z-50 flex h-screen w-full items-center justify-center bg-auth-paper/90 text-center backdrop-blur-md">
                    <div className="flex flex-col gap-4 p-4">
                        <img src={Logo} className="m-auto w-32" alt="Upspring" />
                        <p className="text-lg text-auth-ink">We do not support mobile devices yet.</p>
                        <p className="text-lg text-auth-ink">Please use a desktop browser.</p>
                    </div>
                </div>
            )}

            <SplitAuthLayout
                slide={SIGN_IN_SLIDE}
                maxWidth={400}
                footer={<LegalApproval />}
            >
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    className="relative flex flex-col gap-6"
                >
                    {isLoading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-auth-paper/70">
                            <CircleLoader className="left-0 top-0" />
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <MicroLabel>Sign in</MicroLabel>
                        <h1 className="text-3xl font-light leading-[1.05] tracking-[-0.02em] text-auth-ink">
                            Welcome back
                        </h1>
                    </div>

                    <div className="flex w-full justify-center">
                        <GoogleLogin
                            width="400"
                            text="continue_with"
                            theme="outline"
                            logo_alignment="left"
                            onSuccess={handleGoogleLogin}
                            onError={handleGoogleLoginError}
                        />
                    </div>

                    <div className="relative flex items-center">
                        <span className="h-px flex-1 bg-auth-rule" />
                        <span className="px-3 font-mono text-[11px] uppercase tracking-[0.14em] text-auth-muted">or</span>
                        <span className="h-px flex-1 bg-auth-rule" />
                    </div>

                    <form onSubmit={handleSubmit(manuallyLogIn)} className="flex flex-col gap-4">
                        <AuthField label="Email" required error={errors.email?.message}>
                            <AuthInput
                                type="email"
                                placeholder="you@company.com"
                                error={errors.email?.message}
                                {...register('email')}
                            />
                        </AuthField>
                        <AuthField label="Password" required error={errors.password?.message}>
                            <AuthInput
                                type="password"
                                placeholder="••••••••"
                                error={errors.password?.message}
                                {...register('password')}
                            />
                        </AuthField>
                        <AuthButton type="submit" variant="primary" className="mt-1 w-full">
                            Sign in
                        </AuthButton>
                        <p className="text-center text-sm text-auth-muted">
                            New here?{' '}
                            <button
                                type="button"
                                onClick={() => navigate('/auth/register')}
                                className="text-auth-ink underline-offset-4 hover:underline"
                            >
                                Create account
                            </button>
                        </p>
                    </form>
                </motion.div>
            </SplitAuthLayout>
        </>
    )
}

export default SignInPage
